import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import { listTemplates } from "../../api/templates";
import { listLocations } from "../../api/locations";
import { listSeasons } from "../../api/seasons";
import TemplateDetail from "./TemplateDetail";

export default function TemplatesAdmin() {
  const [rows, setRows] = useState([]);
  const [locations, setLocations] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [detailFor, setDetailFor] = useState(null); // null | "new" | { id }

  useEffect(() => {
    listLocations({ activeOnly: false }).then((d) => setLocations(d || [])).catch(() => {});
    listSeasons({ activeOnly: false }).then((d) => setSeasons(d || [])).catch(() => {});
  }, []);

  const fetchAll = useCallback(() => {
    setLoading(true);
    setError("");
    listTemplates({ activeOnly: false })
      .then((d) => setRows(Array.isArray(d) ? d : []))
      .catch((e) => setError(e.message || "Failed to load templates"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const locationName = useMemo(() => {
    const m = {};
    locations.forEach((l) => { m[l.id] = l.name; });
    return m;
  }, [locations]);
  const seasonName = useMemo(() => {
    const m = {};
    seasons.forEach((s) => { m[s.id] = s.name; });
    return m;
  }, [seasons]);

  if (detailFor === "new") {
    return (
      <TemplateDetail
        templateId={null}
        locations={locations}
        seasons={seasons}
        onClose={() => { setDetailFor(null); fetchAll(); }}
      />
    );
  }
  if (detailFor && detailFor.id) {
    return (
      <TemplateDetail
        templateId={detailFor.id}
        locations={locations}
        seasons={seasons}
        onClose={() => { setDetailFor(null); fetchAll(); }}
      />
    );
  }

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>Templates</Typography>
        <Button startIcon={<RefreshIcon />} onClick={fetchAll}>Refresh</Button>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDetailFor("new")}>New Template</Button>
      </Stack>

      {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Default Nights</TableCell>
              <TableCell>Default Location</TableCell>
              <TableCell>Default Season</TableCell>
              <TableCell>Active</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} align="center"><CircularProgress size={24} /></TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={6} align="center">No templates</TableCell></TableRow>
            ) : rows.map((row) => (
              <TableRow
                key={row.id} hover
                sx={{ cursor: "pointer" }}
                onClick={() => setDetailFor({ id: row.id })}
              >
                <TableCell>{row.name}</TableCell>
                <TableCell>{row.description || ""}</TableCell>
                <TableCell>{row.defaultNights ?? ""}</TableCell>
                <TableCell>{row.defaultLocationId ? locationName[row.defaultLocationId] || row.defaultLocationId : ""}</TableCell>
                <TableCell>{row.defaultSeasonId ? seasonName[row.defaultSeasonId] || row.defaultSeasonId : ""}</TableCell>
                <TableCell>{row.isActive ? "Yes" : "No"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
